import { Box, Typography } from "@mui/material"
import PodInfo from "../components/PodInfo"
import DatasourceNameCell from "../components/DatasourceNameCell"

const { Link } = require("react-router-dom")
const { countFormatterWithCommas, multipurposeSortComparator, getPercentageSkippedOverTotal, graphSlopeComparator, dateFormatter, dataSizeFormatter } = require("./helpers")
const dayjs = require("dayjs")

// Type badge color mapping
const TYPE_COLORS = {
  'SMB': { color: '#7C3AED', bg: '#F5F3FF' },
  'OUTLOOK': { color: '#2563EB', bg: '#EFF6FF' },
  'ONE_DRIVE': { color: '#0891B2', bg: '#ECFEFF' },
  'SHAREPOINT': { color: '#0D9488', bg: '#F0FDFA' },
  'SHAREPOINT1': { color: '#0D9488', bg: '#F0FDFA' },
  'MS_TEAMS': { color: '#4F46E5', bg: '#EEF2FF' },
  'COPILOT': { color: '#7C3AED', bg: '#FAF5FF' },
  'GMAIL': { color: '#DC2626', bg: '#FEF2F2' },
  'GOOGLE_DRIVE': { color: '#16A34A', bg: '#F0FDF4' },
  'MSSQL': { color: '#EA580C', bg: '#FFF7ED' },
  'SNOWFLAKE': { color: '#0284C7', bg: '#F0F9FF' },
  'CSV_DATASOURCE': { color: '#6B7280', bg: '#F9FAFB' },
  'GENERIC': { color: '#6B7280', bg: '#F3F4F6' },
  'SLACK': { color: '#9333EA', bg: '#FAF5FF' },
};
const formatTypeName = (val) => val ? val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\bSmb\b/g, 'SMB').replace(/\bMs /g, 'MS ').replace(/\bMssql\b/g, 'MSSQL').replace(/\bCsv /g, 'CSV ') : '';
const renderTypeBadge = (p) => {
  if (!p.value) return '';
  const tc = TYPE_COLORS[p.value] || { color: '#374151', bg: '#F3F4F6' };
  return (
    <Box sx={{ backgroundColor: tc.bg, color: tc.color, borderRadius: '6px', px: 1, py: 0.25, fontSize: '0.7rem', fontWeight: 700, display: 'inline-block', letterSpacing: '0.02em', border: `1px solid ${tc.color}20` }}>
      {formatTypeName(p.value)}
    </Box>
  );
};

const renderCellDatasourceName = (cellValue) => {
  const status = cellValue.row.podStatus || cellValue.row.status || 'Unknown';

  let dotColor = '#10B981';
  if (status && (status.includes('Fail') || status.includes('Error') || status.includes('Crash'))) {
    dotColor = '#EF4444';
  } else if (status && (status.includes('Pending') || status.includes('Creating'))) {
    dotColor = '#F59E0B';
  } else if (status === 'Unknown') {
    dotColor = '#9CA3AF';
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: dotColor,
          flexShrink: 0,
        }}
      />
      <DatasourceNameCell datasourceId={cellValue.row["datasourceId"]} datasourceName={cellValue.value} />
    </Box>
  )
}

// Status badge component for cleaner rendering
const StatusBadge = ({ value, type = 'default' }) => {
  let color = '#374151';
  let bgColor = '#F3F4F6';

  if (type === 'result') {
    if (value === "Failed") {
      color = "#DC2626";
      bgColor = "#FEE2E2";
    } else if (value === "Succeeded") {
      color = "#059669";
      bgColor = "#D1FAE5";
    }
  } else if (type === 'status') {
    if (value === "Disconnected") {
      color = "#DC2626";
      bgColor = "#FEE2E2";
    } else if (value === "Running") {
      color = "#059669";
      bgColor = "#D1FAE5";
    } else if (value === "Pending") {
      color = "#D97706";
      bgColor = "#FEF3C7";
    }
  }

  return (
    <Typography
      sx={{
        fontWeight: 500,
        fontSize: '0.75rem',
        color: color,
        backgroundColor: bgColor,
        borderRadius: '6px',
        px: 1.5,
        py: 0.5,
        display: 'inline-block',
        lineHeight: 1.4,
      }}
    >
      {value || '--'}
    </Typography>
  )
}

const graphSlopeColumn = {
  field: 'graphSlope',
  headerName: 'Graph slope',
  width: 100,
  getSortComparator: graphSlopeComparator
}

const SAMPLE_DATASET_CLUSTERS = [
  {
    "clusterName": "Inspira",
    "id": 1,
    "overallPending": 0,
    "overallScanned": 0,
    "overallSkipped": 0,
    "scanSpeedDays1": -11355084,
    "scanSpeedDays5": 0.0
  },
  {
    "clusterName": "Snapfinance",
    "id": 2,
    "notReadyDatasources": null,
    "overallPending": 199290128,
    "overallScanned": 16577025,
    "overallSkipped": 8045534,
    "scanSpeedDays1": 21515,
    "scanSpeedDays5": 441768.2,
    "timestamp": "2024-08-22T00:01:59.293051089Z",
    "totalDatasourceConnected": 19
  },
  {
    "clusterName": "PlanetCourier",
    "id": 3,
    "notReadyDatasources": null,
    "overallPending": 35,
    "overallScanned": 278233,
    "overallSkipped": 206335,
    "scanSpeedDays1": 608,
    "scanSpeedDays5": 404.0,
    "timestamp": "2024-08-22T00:01:08.985212656Z",
    "totalDatasourceConnected": 9
  }]

const HomePageColumns = [
  {
    field: 'id',
    headerName: 'Id',
    width: 60,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'clusterName',
    headerName: 'Cluster name',
    flex: 1,
    minWidth: 260,
    cellClassName: 'cluster-name-cell',
    renderCell: (cellValue) => {
      const isHealthy = !cellValue.row.notReadyDatasources || cellValue.row.notReadyDatasources === 0;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: isHealthy ? '#10B981' : '#EF4444',
              flexShrink: 0,
            }}
          />
          <Typography
            component={Link}
            to={`/dashboard/${cellValue.value}/datasources/overview`}
            sx={{
              textDecoration: 'none',
              fontWeight: 500,
              color: '#111827',
              fontSize: '0.875rem',
              '&:hover': {
                color: '#2563EB',
                textDecoration: 'underline'
              }
            }}
          >
            {cellValue.value}
          </Typography>
        </Box>
      )
    }
  },
  {
    field: 'timestamp',
    headerName: 'Last Update',
    width: 160,
    valueFormatter: dateFormatter
  },
  {
    field: 'totalDatasourceConnected',
    headerName: 'Connected',
    width: 100,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'notReadyDatasources',
    headerName: 'Unready',
    width: 90,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'rescanPending',
    headerName: 'Rescan Pending',
    width: 130,
    headerAlign: 'right',
    align: 'right',
    valueFormatter: countFormatterWithCommas,
  },
  {
    field: 'overallPending',
    headerName: 'Overall Pending',
    width: 130,
    headerAlign: 'right',
    align: 'right',
    valueFormatter: countFormatterWithCommas,
    getSortComparator: multipurposeSortComparator
  },
  {
    field: 'overallScanned',
    headerName: 'Overall Scanned',
    width: 130,
    headerAlign: 'right',
    align: 'right',
    valueFormatter: countFormatterWithCommas,
    getSortComparator: multipurposeSortComparator
  },
  {
    field: 'overallSkipped',
    headerName: 'Overall Skipped',
    width: 130,
    headerAlign: 'right',
    align: 'right',
    valueFormatter: countFormatterWithCommas,
    getSortComparator: multipurposeSortComparator
  },
  {
    field: 'processedGb',
    headerName: 'Processed',
    width: 110,
    headerAlign: 'right',
    align: 'right',
    valueFormatter: dataSizeFormatter
  },
  {
    field: 'skippedGb',
    headerName: 'Skipped',
    width: 110,
    headerAlign: 'right',
    align: 'right',
    valueFormatter: dataSizeFormatter
  },
  {
    field: 'totalGb',
    headerName: 'Total Data',
    width: 110,
    headerAlign: 'right',
    align: 'right',
    valueFormatter: dataSizeFormatter
  },
  {
    field: 'percentageSkipped',
    headerName: 'Skip Ratio',
    width: 100,
    headerAlign: 'right',
    align: 'right',
    valueGetter: getPercentageSkippedOverTotal,
    getSortComparator: multipurposeSortComparator
  },
  {
    field: 'processedCountDays1',
    headerName: '1d Avg',
    width: 100,
    headerAlign: 'right',
    align: 'right',
    valueFormatter: countFormatterWithCommas,
    getSortComparator: multipurposeSortComparator
  },
  {
    field: 'processedCountDays5',
    headerName: '5d Avg',
    width: 100,
    headerAlign: 'right',
    align: 'right',
    valueFormatter: countFormatterWithCommas,
    getSortComparator: multipurposeSortComparator
  },
  {
    field: 'pendingChangeDays1',
    headerName: 'Daily Change',
    width: 110,
    headerAlign: 'right',
    align: 'right',
    valueFormatter: countFormatterWithCommas,
    getSortComparator: multipurposeSortComparator
  },
  {
    field: 'clusterVersion',
    headerName: 'Version',
    width: 120,
  },
  {
    field: 'clusterUpdateTime',
    headerName: 'Version Update',
    width: 140,
    valueFormatter: dateFormatter
  },
  {
    field: 'nodeInfo',
    headerName: 'Node Info',
    width: 120,
    valueGetter: (value, row) => {
      return `${row.nodeCount}-${row.nodeType}`
    }
  },
  {
    field: 'crashlogPresent',
    headerName: 'Crash Log',
    width: 100,
    headerAlign: 'center',
    align: 'center',
    renderCell: (cellValue) => {
      if (!cellValue.value) return <Typography sx={{ color: '#9CA3AF', fontSize: '0.875rem' }}>--</Typography>;
      if (cellValue.value.toLowerCase() === "yes")
        return (
          <Link
            to={`/crashlog/${cellValue.row.clusterName}`}
            style={{
              color: '#DC2626',
              fontWeight: 500,
              textDecoration: 'none',
              fontSize: '0.875rem',
            }}
          >
            Yes
          </Link>
        )
      else
        return <Typography sx={{ color: '#6B7280', fontSize: '0.875rem' }}>{cellValue.value}</Typography>
    }
  }
]

const overallStatsColumns = [];

const getColumnsUnstructuredPendingCount = () => {
  const columns = [
    {
      field: 'id',
      headerName: 'Id',
      width: 50,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'datasourceName',
      headerName: 'Name',
      flex: 1,
      minWidth: 220,
      renderCell: renderCellDatasourceName
    },
    {
      field: 'datasourceType',
      headerName: 'Type',
      width: 130,
      renderCell: renderTypeBadge,
    },
    {
      field: 'podStatus',
      headerName: 'Status',
      width: 130,
      renderCell: (cellValue) => (<PodInfo podStatus={cellValue.value} podData={cellValue.row["podInfo"]} />)
    },
    {
      field: 'totalGb',
      headerName: 'Total Data',
      width: 100,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: dataSizeFormatter
    },
    {
      field: 'totalUsers',
      headerName: 'Users',
      width: 80,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: countFormatterWithCommas
    },
  ]
  for (let i = 0; i < 14; i++) {
    let currentdate = dayjs(new Date())
    columns.push({
      field: `pendingCountDay${i}`,
      headerName: currentdate.subtract(i, 'days').format('MM/DD'),
      width: 100,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: countFormatterWithCommas,
      renderCell: (cellValue) => {
        const currentCount = Number(cellValue.value)
        const prevCount = Number(cellValue.row[`pendingCountDay${i + 1}`])

        let color = "#059669"
        let diff = currentCount - prevCount
        const copyDiff = diff
        if (diff >= 0) {
          color = "#DC2626"
          diff = "+" + countFormatterWithCommas(diff)
        } else {
          diff = countFormatterWithCommas(diff)
        }

        let percentageChange = Math.round(Math.abs((copyDiff / prevCount) * 10000))
        percentageChange = percentageChange / 100
        if (!isFinite(percentageChange)) {
          percentageChange = ""
        } else {
          percentageChange = "(" + percentageChange + "%)"
        }

        if (isNaN(currentCount) || isNaN(prevCount) || (prevCount === 0 && copyDiff === 0)) {
          return (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}>
              <Typography sx={{ fontWeight: 500, color: '#374151', fontSize: '0.8125rem' }}>
                {countFormatterWithCommas(cellValue.value) || '--'}
              </Typography>
            </Box>
          )
        }

        return (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            lineHeight: 1.4,
          }}>
            <Typography sx={{ fontWeight: 500, color: '#374151', fontSize: '0.8125rem' }}>
              {countFormatterWithCommas(cellValue.value)}
            </Typography>
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 500, color: color }}>
              {diff}{percentageChange}
            </Typography>
          </Box>
        )
      }
    })
  }
  columns.push(graphSlopeColumn)

  return columns
}

const getColumnsUnstructuredProcessedCount = () => {
  const columns = [
    {
      field: 'id',
      headerName: 'Id',
      width: 50,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'datasourceName',
      headerName: 'Name',
      flex: 1,
      minWidth: 220,
      renderCell: renderCellDatasourceName
    },
    {
      field: 'datasourceType',
      headerName: 'Type',
      width: 130,
      renderCell: renderTypeBadge,
    },
    {
      field: 'podStatus',
      headerName: 'Status',
      width: 130,
      renderCell: (cellValue) => (<PodInfo podStatus={cellValue.value} podData={cellValue.row["podInfo"]} />)
    },
    {
      field: 'totalGb',
      headerName: 'Total Data',
      width: 100,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: dataSizeFormatter
    },
    {
      field: 'totalUsers',
      headerName: 'Users',
      width: 80,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: countFormatterWithCommas
    },
  ]
  for (let i = 0; i < 14; i++) {
    let currentdate = dayjs(new Date())
    columns.push({
      field: `processedCountDay${i}`,
      headerName: currentdate.subtract(i, 'days').format('MM/DD'),
      width: 100,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: countFormatterWithCommas,
      renderCell: (cellValue) => {
        const currentCount = Number(cellValue.value)
        const prevCount = Number(cellValue.row[`processedCountDay${i + 1}`])

        let color = "#059669"
        let diff = currentCount - prevCount
        const copyDiff = diff
        if (diff <= 0) {
          color = "#DC2626"
          diff = countFormatterWithCommas(diff)
        } else {
          diff = "+" + countFormatterWithCommas(diff)
        }

        let percentageChange = Math.round(Math.abs((copyDiff / prevCount) * 10000))
        percentageChange = percentageChange / 100
        if (!isFinite(percentageChange)) {
          percentageChange = ""
        } else {
          percentageChange = "(" + percentageChange + "%)"
        }

        if (isNaN(currentCount) || isNaN(prevCount) || (prevCount === 0 && copyDiff === 0)) {
          return (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}>
              <Typography sx={{ fontWeight: 500, color: '#374151', fontSize: '0.8125rem' }}>
                {countFormatterWithCommas(cellValue.value) || '--'}
              </Typography>
            </Box>
          )
        }

        return (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            lineHeight: 1.4,
          }}>
            <Typography sx={{ fontWeight: 500, color: '#374151', fontSize: '0.8125rem' }}>
              {countFormatterWithCommas(cellValue.value)}
            </Typography>
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 500, color: color }}>
              {diff}{percentageChange}
            </Typography>
          </Box>
        )
      }
    })
  }
  columns.push(graphSlopeColumn)
  return columns
}

const getColumnsUnstructuredRescanPendingCount = () => {
  const columns = [
    {
      field: 'id',
      headerName: 'Id',
      width: 50,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'datasourceName',
      headerName: 'Name',
      flex: 1,
      minWidth: 220,
      renderCell: renderCellDatasourceName
    },
    {
      field: 'datasourceType',
      headerName: 'Type',
      width: 130,
      renderCell: renderTypeBadge,
    },
    {
      field: 'podStatus',
      headerName: 'Status',
      width: 130,
      renderCell: (cellValue) => (<PodInfo podStatus={cellValue.value} podData={cellValue.row["podInfo"]} />)
    },
    {
      field: 'totalGb',
      headerName: 'Total Data',
      width: 100,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: dataSizeFormatter
    },
    {
      field: 'totalUsers',
      headerName: 'Users',
      width: 80,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: countFormatterWithCommas
    },
  ]
  // Rescan pending count shows last 7 days
  for (let i = 0; i < 7; i++) {
    let currentdate = dayjs(new Date())
    columns.push({
      field: `rescanPendingCountDay${i}`,
      headerName: currentdate.subtract(i, 'days').format('MM/DD'),
      width: 100,
      headerAlign: 'center',
      align: 'center',
      valueFormatter: countFormatterWithCommas,
      renderCell: (cellValue) => {
        const currentCount = Number(cellValue.value)
        const prevCount = Number(cellValue.row[`rescanPendingCountDay${i + 1}`])

        let color = "#059669"
        let diff = currentCount - prevCount
        const copyDiff = diff
        // For rescan pending, increase is bad (red), decrease is good (green)
        if (diff >= 0) {
          color = "#DC2626"
          diff = "+" + countFormatterWithCommas(diff)
        } else {
          diff = countFormatterWithCommas(diff)
        }

        let percentageChange = Math.round(Math.abs((copyDiff / prevCount) * 10000))
        percentageChange = percentageChange / 100
        if (!isFinite(percentageChange)) {
          percentageChange = ""
        } else {
          percentageChange = "(" + percentageChange + "%)"
        }

        if (isNaN(currentCount) || isNaN(prevCount) || (prevCount === 0 && copyDiff === 0)) {
          return (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}>
              <Typography sx={{ fontWeight: 500, color: '#374151', fontSize: '0.8125rem' }}>
                {countFormatterWithCommas(cellValue.value) || '--'}
              </Typography>
            </Box>
          )
        }

        return (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            lineHeight: 1.4,
          }}>
            <Typography sx={{ fontWeight: 500, color: '#374151', fontSize: '0.8125rem' }}>
              {countFormatterWithCommas(cellValue.value)}
            </Typography>
            <Typography sx={{ fontSize: '0.625rem', fontWeight: 500, color: color }}>
              {diff}{percentageChange}
            </Typography>
          </Box>
        )
      }
    })
  }
  columns.push(graphSlopeColumn)
  return columns
}

const getColumnsStructured = () => {
  return [
    {
      field: 'id',
      headerName: 'Id',
      width: 50,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'datasourceName',
      headerName: 'Name',
      flex: 1,
      minWidth: 220,
      renderCell: renderCellDatasourceName
    },
    {
      field: 'datasourceType',
      headerName: 'Type',
      width: 130,
      renderCell: renderTypeBadge,
    },
    {
      field: 'podStatus',
      headerName: 'Status',
      width: 130,
      renderCell: (cellValue) => (<PodInfo podStatus={cellValue.value} podData={cellValue.row["podInfo"]} />)
    },
    {
      field: 'totalGb',
      headerName: 'Total Data',
      width: 100,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: dataSizeFormatter
    },
    {
      field: 'totalUsers',
      headerName: 'Users',
      width: 80,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: countFormatterWithCommas
    },
    {
      field: 'lastScanResult',
      headerName: 'Last Result',
      width: 110,
      headerAlign: 'center',
      align: 'center',
      renderCell: (cellValue) => <StatusBadge value={cellValue.value} type="result" />
    },
    {
      field: 'duration',
      headerName: 'Duration',
      width: 120,
    },
    {
      field: 'lastSuccessfulScan',
      headerName: 'Last Success',
      width: 160,
    },
    {
      field: 'nextScheduledScan',
      headerName: 'Next Scan',
      width: 160,
    },
    {
      field: 'status',
      headerName: 'Connection',
      width: 110,
      headerAlign: 'center',
      align: 'center',
      renderCell: (cellValue) => <StatusBadge value={cellValue.value} type="status" />
    },
    {
      field: 'withoutPermissions',
      headerName: 'No Permission',
      width: 120,
      headerAlign: 'center',
      align: 'center',
    }
  ]
}

const getColumnsOverallStats = () => {
  if (overallStatsColumns.length === 0)
    overallStatsColumns.push(...HomePageColumns.filter((column) => (column.field !== "clusterName")))

  return overallStatsColumns
}

const CAPABILITY = {
  VIEW_CLUSTER_INFORMATION: "view_cluster_information",
  VIEW_CLUSTER_UI: "view_cluster_ui",
  MANAGE_USERS: "manage_users",
  MANAGE_CLUSTER_TUNNELS: "manager_cluster_tunnel"
}

export {
  HomePageColumns,
  SAMPLE_DATASET_CLUSTERS,
  getColumnsStructured,
  getColumnsUnstructuredPendingCount,
  getColumnsUnstructuredProcessedCount,
  getColumnsUnstructuredRescanPendingCount,
  getColumnsOverallStats,
  CAPABILITY
}
